import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle,
  CheckCircle,
  AlertTriangle,
  Send,
  Shield,
  Lock,
  ExternalLink,
  ArrowLeft,
  ArrowRight,
  Loader,
  Info,
  X,
  Sun,
  Moon,
  Mail,
  User,
  ThumbsUp,
  Star,
  BarChart2,
  Award,
  Clock,
  Calendar,
  Heart,
  Layout,
  Settings,
  Archive,
  LifeBuoy,
  Smartphone,
  Tablet,
  Monitor,
  SkipForward
} from 'lucide-react';

// Import landing page images
import landingImageDesktop from './assets/img/image2.png';
import landingImageMobile from './assets/img/image.png';

// Base API URL - should be configurable
const API_BASE_URL = 'https://lager-app-api.sunrise-avengers.ch';

// Mock-up image paths - replace with your actual images
const MOCKUP_IMAGES = [
  '/assets/img/moks/iPhone 15 Pro Max (4).png', // Smartphone mockup 1
  '/assets/img/moks/wmremove-transformed (2).jpeg', // Smartphone mockup 2
  '/assets/img/moks/c710433775f21442d7b475e6d4113ffe (1).png', // Tablet mockup
  '/assets/img/moks/iPhone 15 Pro Max.png',  // Desktop mockup
  '/assets/img/Bildschirmfoto 2025-10-23 um 07.24.14.png', // App screenshot 1
  '/assets/img/Bildschirmfoto 2025-10-27 um 10.10.29.png', // App screenshot 2
  '/assets/img/cAmPP-feadback-header.png', // Feedback header
];

// Device type descriptions to match mockup images
const MOCKUP_DESCRIPTIONS = [
  "Mobile Experience",
  "Responsive Interface",
  "Tablet Optimization",
  "Desktop Compatibility",
  "Just some pic of the app",
  "Just some pic of the app",
  "Just some pic of the app"
];

const FeedbackFormWithMockups = ({ onClose }) => {
  // ====== STATE MANAGEMENT ======
  // Main state
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  
  // UI states
  const [showLandingPage, setShowLandingPage] = useState(true); // Neue Landing Page State
  const [activeTab, setActiveTab] = useState('feedback');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false); // Standardmäßig eingeklappt
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [viewMode, setViewMode] = useState('standard'); // 'standard', 'minimal', 'detailed'
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [initialLoad, setInitialLoad] = useState(true);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  
  // Mockup animation states
  const [currentMockupIndex, setCurrentMockupIndex] = useState(0);
  const [mockupAnimating, setMockupAnimating] = useState(false);
  const [isMockupVisible, setIsMockupVisible] = useState(true);
  
  // Tracking state for optimized rendering
  const [activeTextareaId, setActiveTextareaId] = useState(null);
  const [focusedElement, setFocusedElement] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const cursorPositionRef = useRef({});
  const typingTimeoutRef  = useRef(null);
  const textValuesRef = useRef({}); // Store current textarea values
  // Refs
  const formRef = useRef(null);
  const textareaRefs = useRef({});
  const questionRefs = useRef({});
  const mainContentRef = useRef(null);
  const mockupRef = useRef(null);
  const mockupIntervalRef = useRef(null);

  // ====== EFFECTS ======
  // Initialize theme and check for previous submission
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('feedbackDarkMode');
    // Standardmäßig Dark Mode, außer es wurde explizit Light Mode gespeichert
    setDarkMode(savedDarkMode === 'false' ? false : true);
    
    // Check if user has already submitted feedback
    const hasSubmitted = localStorage.getItem('feedbackSubmitted') === 'true';
    setAlreadySubmitted(hasSubmitted);
    
    addStylesToDocument();
    
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setIsDesktop(width >= 1024);
      
      // Auto-hide sidebar on smaller screens (Tablet & Mobile)
      if (width < 1024 && showSidebar) {
        setShowSidebar(false);
      }
      // Sidebar bleibt auf Desktop standardmäßig eingeklappt
      // User kann sie manuell öffnen
    };
    
    handleResize(); // Initialize on mount
    window.addEventListener('resize', handleResize);
    
    // Set initial load to false after a delay
    const initialTimer = setTimeout(() => {
      setInitialLoad(false);
    }, 2000);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(initialTimer);
    };
  }, []);
  
  // Update body/html/root background colors when darkMode changes
  useEffect(() => {
    const backgroundColor = darkMode ? '#121212' : '#F8F9FA';
    
    // Force update with setProperty for higher priority
    document.body.style.setProperty('background-color', backgroundColor, 'important');
    document.documentElement.style.setProperty('background-color', backgroundColor, 'important');
    
    // Update #root element if it exists
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.setProperty('background-color', backgroundColor, 'important');
    }
    
    // Also set via setAttribute as backup
    document.body.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);
  
  // Load questions on mount
  useEffect(() => {
    fetchQuestions();
  }, []);
  
  // Update completion percentage
  useEffect(() => {
    if (questions.length === 0) return;
    
    const completedCount = responses.filter(response => 
      (response.starRating !== null && response.starRating !== undefined) || 
      (response.textResponse && response.textResponse.trim() !== '') ||
      response.skipped === true
    ).length;
    
    const percentage = Math.round((completedCount / questions.length) * 100);
    setCompletionPercentage(percentage);
  }, [responses, questions]);
  
  // Mockup carousel animation - simplified
  useEffect(() => {
    if (!isDesktop) return;
    
    // Clear any existing interval
    if (mockupIntervalRef.current) {
      clearInterval(mockupIntervalRef.current);
    }
    
    // Create a new interval that changes mockups every 5 seconds
    mockupIntervalRef.current = setInterval(() => {
      setCurrentMockupIndex((prev) => (prev + 1) % MOCKUP_IMAGES.length);
    }, 5000);
    
    return () => {
      if (mockupIntervalRef.current) {
        clearInterval(mockupIntervalRef.current);
      }
    };
  }, [isDesktop]);
  
  // Scroll to active question in step mode on mobile
  useEffect(() => {
    if (!isDesktop && viewMode === 'step' && questionRefs.current[currentQuestionIndex]) {
      questionRefs.current[currentQuestionIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [currentQuestionIndex, isDesktop, viewMode]);

  // ====== FUNCTIONS ======
  // Toggle dark mode
  const toggleDarkMode = useCallback(() => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('feedbackDarkMode', newMode.toString());
    
    // Update body background immediately with force
    const backgroundColor = newMode ? '#121212' : '#F8F9FA';
    document.body.style.setProperty('background-color', backgroundColor, 'important');
    document.documentElement.style.setProperty('background-color', backgroundColor, 'important');
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.setProperty('background-color', backgroundColor, 'important');
    }
    document.body.setAttribute('data-theme', newMode ? 'dark' : 'light');
  }, [darkMode]);
  
  // Add required styles to document
  const addStylesToDocument = () => {
    if (!document.getElementById('enhanced-feedback-form-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'enhanced-feedback-form-styles';
      styleElement.innerHTML = `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes slideDown {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes slideInLeft {
          from { transform: translateX(-20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        
        @keyframes slideInRight {
          from { transform: translateX(20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes floatUp {
          0% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0); }
        }
        
        @keyframes floatingShadow {
          0% { box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2); }
          50% { box-shadow: 0 20px 45px rgba(0, 0, 0, 0.25); }
          100% { box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2); }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        @keyframes glow {
          0% { box-shadow: 0 0 10px rgba(255, 59, 48, 0.5); }
          50% { box-shadow: 0 0 20px rgba(255, 59, 48, 0.7); }
          100% { box-shadow: 0 0 10px rgba(255, 59, 48, 0.5); }
        }
        
        @keyframes bounce {
          0%, 100% { transform: rotate(-90deg) translateY(0); }
          50% { transform: rotate(-90deg) translateY(-10px); }
        }
        
        @keyframes slideDown {
          0% { transform: translateY(-100%); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes slideUp {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(-100%); opacity: 0; }
        }
        
        @keyframes fadeMockup {
          0% { opacity: 0; transform: scale(0.95) translateY(10px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        
        .hover-scale {
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .hover-scale:hover {
          transform: scale(1.03);
        }
        
        .mockup-container {
          animation: fadeMockup 0.8s ease-out forwards;
        }
        
        .mockup-fade-in {
          opacity: 0;
          animation: fadeMockup 0.8s ease-out forwards;
        }
        
        .mockup-fade-out {
          opacity: 1;
          animation: fadeMockup 0.8s ease-out reverse forwards;
        }
        
        .mockup-pulse {
          animation: pulse 4s ease-in-out infinite;
        }
        
        .mockup-float {
          animation: floatUp 6s ease-in-out infinite, floatingShadow 6s ease-in-out infinite;
        }
        
        /* Glassmorphism effect */
        .glassmorphism {
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }
        
        /* Scrollbar styling */
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(155, 155, 155, 0.5);
          border-radius: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(155, 155, 155, 0.7);
        }
        
        /* Progress bar animation */
        @keyframes progressFill {
          from { width: 0%; }
          to { width: var(--progress-percent); }
        }
        
        .progress-bar-fill {
          animation: progressFill 1.5s ease-out forwards;
        }
        
        /* Focus styles */
        .focus-visible:focus {
          outline: 2px solid #FF3B30;
          outline-offset: 2px;
        }
        
        /* Mobile/tablet adjustments */
        @media (max-width: 1023px) {
          .desktop-only {
            display: none !important;
          }
          
          .desktop-sidebar {
            display: none !important;
          }
          
          .mobile-content {
            padding: 0 !important;
            margin-left: 0 !important;
          }
        }
        
        @media (min-width: 1024px) {
          .mobile-only {
            display: none !important;
          }
        }
        
        /* Textarea autoresize */
        .autosize-textarea {
          overflow: hidden;
          resize: none;
          min-height: 100px;
          transition: height 0.1s ease;
        }
        
        /* Tooltip styles */
        .tooltip {
          position: relative;
        }
        
        .tooltip:hover:after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 5px 10px;
          border-radius: 5px;
          white-space: nowrap;
          font-size: 12px;
          z-index: 10;
          pointer-events: none;
          margin-bottom: 5px;
        }
        
        /* Custom checkbox */
        .custom-checkbox {
          appearance: none;
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 4px;
          outline: none;
          cursor: pointer;
          position: relative;
        }
        
        .custom-checkbox:checked:after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 12px;
          height: 12px;
          border-radius: 2px;
          background: #FF3B30;
        }
        
        /* Star rating animation */
        @keyframes starPop {
          0% { transform: scale(1); }
          50% { transform: scale(1.3); }
          100% { transform: scale(1); }
        }
        
        .star-pop {
          animation: starPop 0.3s ease forwards;
        }
        
        /* Skipped question styles */
        .skipped-question {
          opacity: 0.7;
          position: relative;
        }
        
        .skipped-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: rgba(150, 150, 150, 0.2);
          color: #888;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }
        
        /* Global body background fix for dark mode */
        body {
          background-color: ${darkMode ? '#000000' : '#ffffff'} !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        html {
          background-color: ${darkMode ? '#000000' : '#ffffff'} !important;
        }
        
        #root {
          background-color: ${darkMode ? '#000000' : '#ffffff'} !important;
        }
      `;
      document.head.appendChild(styleElement);
    }
    
    // Update body background when dark mode changes
    document.body.style.backgroundColor = darkMode ? '#000000' : '#ffffff';
    document.documentElement.style.backgroundColor = darkMode ? '#000000' : '#ffffff';
    
    // Also update root
    const root = document.getElementById('root');
    if (root) {
      root.style.backgroundColor = darkMode ? '#000000' : '#ffffff';
    }
  };

  // Fetch questions from API
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      
      // Fetch questions from the API
      const response = await fetch(`${API_BASE_URL}/api/feedback/questions/active`);
      
      if (!response.ok) {
        throw new Error('Failed to load questions');
      }
      
      const data = await response.json();
      setQuestions(data);
      
      // Initialize responses array
      const initialResponses = data.map(question => ({
        question: { id: question.id },
        starRating: question.isStarRating ? null : undefined,
        textResponse: question.isStarRating ? undefined : '',
        skipped: false
      }));
      
      setResponses(initialResponses);
      setTimeout(() => setLoading(false), 700); // Small delay for smoother transition
    } catch (error) {
      console.error('Error fetching questions:', error);
      setError('Could not load the feedback form. Please try again later.');
      setLoading(false);
    }
  };

  // Handle star rating change
  const handleRatingChange = useCallback((questionId, rating) => {
    setResponses(prevResponses => 
      prevResponses.map(response => 
        response.question.id === questionId 
          ? { ...response, starRating: rating, skipped: false }
          : response
      )
    );
  }, []);
  
  // Handle text change - keep textareas completely uncontrolled
  const handleTextChange = useCallback((questionId, text, e) => {
    const textarea = e.target;
    
    // Store ref (used for reading values on submit)
    textareaRefs.current[questionId] = textarea;
    
    // Store current value in ref to survive re-renders
    textValuesRef.current[questionId] = text;
    
    // Auto-resize textarea (pure DOM manipulation, no React state)
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.max(100, textarea.scrollHeight)}px`;
    
    // Pause mockup carousel while typing to prevent re-renders
    if (mockupIntervalRef.current) {
      clearInterval(mockupIntervalRef.current);
    }
    
    // Don't update any React state to prevent re-renders
  }, []);
  
  // Handle question skip
  const handleSkipQuestion = useCallback((questionId) => {
    setResponses(prevResponses => 
      prevResponses.map(response => 
        response.question.id === questionId 
          ? { ...response, skipped: true }
          : response
      )
    );
    
    // Move to next question in step mode
    if (viewMode === 'step' && currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  }, [currentQuestionIndex, questions.length, viewMode]);
  
  // Handle textarea focus events
  const handleTextareaFocus = useCallback((questionId, e) => {
    setFocusedElement(questionId);
    textareaRefs.current[questionId] = e.target;
    
    // Add visual styling
    e.target.style.borderColor = '#FF3B30';
    e.target.style.boxShadow = darkMode 
      ? '0 0 0 2px rgba(255, 59, 48, 0.2)'
      : '0 0 0 2px rgba(255, 59, 48, 0.1)';
    
    // Add typing animation class
    e.target.parentElement.classList.add('textarea-typing');
  }, [darkMode]);
  
  // Handle textarea blur events
  const handleTextareaBlur = useCallback((questionId, e) => {
    setFocusedElement(null);
    
    // Only reset visual styling
    e.target.style.borderColor = darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    e.target.style.boxShadow = 'none';
    
    // Remove typing animation class
    e.target.parentElement.classList.remove('textarea-typing');
    
    // Resume mockup carousel after typing stops
    if (isDesktop && !mockupIntervalRef.current) {
      mockupIntervalRef.current = setInterval(() => {
        setMockupAnimating(true);
        setTimeout(() => {
          setCurrentMockupIndex((prev) => (prev + 1) % MOCKUP_IMAGES.length);
          setTimeout(() => {
            setMockupAnimating(false);
          }, 300);
        }, 500);
      }, 5000);
    }
  }, [darkMode, isDesktop]);
  
  // Navigate to next/previous question in step mode
  const navigateQuestion = (direction) => {
    if (direction === 'next' && currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else if (direction === 'prev' && currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };
  
  // Change view mode
  const changeViewMode = (mode) => {
    setViewMode(mode);
    
    // Reset to first question if switching to step mode
    if (mode === 'step') {
      setCurrentQuestionIndex(0);
    }
  };

  // Submit feedback with validation
  const submitFeedback = async () => {
    try {
      // Check if already submitted
      if (alreadySubmitted) {
        setError("You have already submitted feedback. Thank you for your participation!");
        return;
      }
      
      setSubmitting(true);
      setError(null);
      
      // Read current values from DOM textareas before validation
      const currentResponses = responses.map(response => {
        const question = questions.find(q => q.id === response.question.id);
        
        // For text questions, get the current value from DOM
        if (question && !question.isStarRating) {
          const textarea = textareaRefs.current[question.id];
          if (textarea) {
            return {
              ...response,
              textResponse: textarea.value
            };
          }
        }
        
        return response;
      });
      
      // Validate responses - allowing skipped questions
      let isValid = true;
      let errorMessage = '';
      let firstInvalidIndex = -1;
      
      currentResponses.forEach((response, index) => {
        const question = questions[index];
        
        // Skip validation for skipped questions
        if (response.skipped) return;
        
        if (question.isStarRating && (response.starRating === null || response.starRating === undefined)) {
          if (isValid) {
            firstInvalidIndex = index;
          }
          isValid = false;
          errorMessage = `Please provide a rating for "${question.questionText}" or skip the question`;
        } else if (!question.isStarRating && (!response.textResponse || !response.textResponse.trim())) {
          if (isValid) {
            firstInvalidIndex = index;
          }
          isValid = false;
          errorMessage = `Please provide a text response for "${question.questionText}" or skip the question`;
        }
      });
      
      if (!isValid) {
        setError(errorMessage);
        setSubmitting(false);
        
        // Jump to the first invalid question in step mode
        if (viewMode === 'step') {
          setCurrentQuestionIndex(firstInvalidIndex);
        } else if (questionRefs.current[firstInvalidIndex]) {
          // Scroll to the first invalid question in standard mode
          questionRefs.current[firstInvalidIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
        
        return;
      }
      
      // Filter out skipped questions
      const filteredResponses = currentResponses
        .filter(response => !response.skipped)
        .map(({skipped, ...rest}) => rest); // Remove skipped property
      
      // Submit responses to API
      const response = await fetch(`${API_BASE_URL}/api/feedback/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filteredResponses),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }
      
      // Show success message and clear form
      setSuccess(true);
      setError(null);
      setSubmitting(false);
      
      // Mark as submitted in localStorage
      localStorage.setItem('feedbackSubmitted', 'true');
      setAlreadySubmitted(true);
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('Failed to submit feedback. Please try again later.');
      setSubmitting(false);
    }
  };
  
  // Reset form to submit more feedback (admin function only)
  const resetForm = () => {
    // Admin function - would require admin credentials in a real app
    localStorage.removeItem('feedbackSubmitted');
    setAlreadySubmitted(false);
    
    setSuccess(false);
    setError(null);
    fetchQuestions();
    setActiveTextareaId(null);
    setCurrentQuestionIndex(0);
    setHasInteracted(false);
    textareaRefs.current = {};
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // ====== COMPONENTS ======
  // Error notification component
  const ErrorNotification = ({ message }) => {
    if (!message) return null;
    
    return (
      <div style={{
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        maxWidth: '400px',
        width: 'calc(100% - 32px)',
        background: darkMode ? 'rgba(255, 69, 58, 0.15)' : 'rgba(255, 69, 58, 0.1)',
        border: `1px solid ${darkMode ? 'rgba(255, 69, 58, 0.3)' : 'rgba(255, 69, 58, 0.2)'}`,
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        zIndex: 1000,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
      }}>
        <div style={{
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <AlertTriangle size={24} color="#FF453A" />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{
            margin: 0,
            fontSize: '14px',
            lineHeight: 1.5,
            color: darkMode ? '#f3f4f6' : '#1f2937',
          }}>{message}</p>
        </div>
        <button
          style={{
            background: 'transparent',
            border: 'none',
            display: 'flex',
            padding: '4px',
            cursor: 'pointer',
            color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.4)',
          }}
          onClick={() => setError(null)}
        >
          <X size={16} />
        </button>
      </div>
    );
  };
  
  // SunriseRating component
  const SunriseRating = React.memo(({ value = 0, onChange, readOnly = false, size = 30 }) => {
    const [hoverValue, setHoverValue] = useState(0);
    
    const handleMouseEnter = (index) => {
      if (readOnly) return;
      setHoverValue(index);
    };
  
    const handleMouseLeave = () => {
      if (readOnly) return;
      setHoverValue(0);
    };
  
    const handleClick = (index) => {
      if (readOnly) return;
      onChange(index);
    };
    
    // Get rating text based on hover or selected value
    const getRatingText = (rating) => {
      if (rating === 0) return '';
      if (rating <= 2) return 'Very Poor';
      if (rating <= 4) return 'Poor';
      if (rating <= 6) return 'Fair';
      if (rating <= 8) return 'Good';
      if (rating <= 9) return 'Very Good';
      return 'Excellent';
    };
    
    const currentRating = hoverValue || value;
    const ratingText = getRatingText(currentRating);
  
    // Sunrise logo SVG from provided asset
    const sunriseIcon = (filled, iconSize) => (
      <svg
        width={iconSize}
        height={(iconSize * 89) / 99}
        viewBox="0 0 99 89"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          transition: 'all 0.2s ease',
          filter: filled ? 'drop-shadow(0 0 2px rgba(218, 41, 28, 0.5))' : 'none',
        }}
      >
        <g clipPath="url(#clip0_915_12851)">
          <path
            d="M49.4149 0.247231C43.6992 0.246742 38.1118 1.94122 33.3592 5.11636C28.6066 8.2915 24.9023 12.8047 22.7148 18.0852C20.5273 23.3657 19.9548 29.1763 21.0697 34.7821C22.1847 40.388 24.937 45.5373 28.9786 49.5789C33.0201 53.6204 38.1695 56.3727 43.7753 57.4877C49.3812 58.6026 55.1918 58.0302 60.4722 55.8426C65.7527 53.6551 70.2659 49.9508 73.4411 45.1982C76.6162 40.4456 78.3107 34.8582 78.3102 29.1426C78.3023 21.4814 75.2555 14.1364 69.8383 8.71915C64.4211 3.30193 57.076 0.255084 49.4149 0.247231ZM49.4149 6.18056C53.2703 6.17855 57.0641 7.14788 60.446 8.99905C63.828 10.8502 66.689 13.5236 68.7651 16.7723C70.8411 20.021 72.0653 23.7404 72.3245 27.5871C72.5838 31.4338 71.8697 35.2839 70.2483 38.7818H28.579C26.9575 35.2837 26.2434 31.4334 26.5028 27.5865C26.7621 23.7396 27.9865 20.0201 30.0628 16.7713C32.1392 13.5225 35.0006 10.8492 38.3829 8.99816C41.7651 7.14716 45.5592 6.17814 49.4149 6.18056Z"
            fill={filled ? '#DA291C' : '#777777'}
          />
          <path
            fill={filled ? '#DA291C' : '#777777'}
          />
        </g>
        <defs>
          <clipPath id="clip0_915_12851">
            <rect width="98.8888" height="89" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        <div style={{
          display: 'flex', 
          gap: '8px',
          padding: '10px 0',
        }}>
          {[1, 2, 3, 4, 5,6,7,8,9,10].map((index) => (
            <div
              key={index}
              className={(hoverValue || value) >= index ? 'star-pop' : ''}
              style={{
                cursor: readOnly ? 'default' : 'pointer',
                transition: 'transform 0.2s ease, filter 0.2s ease',
                transform: (hoverValue >= index && !readOnly) ? 'scale(1.2)' : 'scale(1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: readOnly && value < index ? 0.5 : 1,
              }}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleClick(index)}
            >
              {sunriseIcon((hoverValue || value) >= index, size)}
            </div>
          ))}
        </div>
        
        {/* Rating text indicator */}
        <div style={{
          textAlign: 'center',
          fontSize: '15px',
          fontWeight: '600',
          color: currentRating <= 2 ? '#FF3B30' : 
                 currentRating <= 4 ? '#FF9500' : 
                 currentRating <= 6 ? '#FFCC00' : 
                 currentRating <= 8 ? '#34C759' : '#007AFF',
          minHeight: '24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: ratingText ? 1 : 0,
          transform: ratingText ? 'translateY(0)' : 'translateY(-5px)',
        }}>
          {ratingText}
        </div>
      </div>
    );
  });
  
  // Progress bar component
  const ProgressBar = ({ percentage }) => (
    <div style={{
      width: '100%',
      height: '6px',
      background: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
      borderRadius: '3px',
      overflow: 'hidden',
      marginBottom: '10px',
    }}>
      <div 
        className="progress-bar-fill"
        style={{
          height: '100%',
          background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
          borderRadius: '3px',
          width: `${percentage}%`,
          '--progress-percent': `${percentage}%`,
          transition: 'width 0.5s ease',
        }}
      ></div>
    </div>
  );
  
  // Simplified Mockup Display Component
  const SimplifiedMockupDisplay = () => {
    // Im Desktop: Ausblenden auf Laptop-Screens (1024-1440px) wenn Sidebar offen ist
    const isLaptopScreen = windowWidth >= 1024 && windowWidth <= 1440;
    if (isDesktop && isLaptopScreen && showSidebar) return null;
    
    const mockupIcons = [
      <Smartphone key="smartphone" size={24} />,
      <Smartphone key="smartphone2" size={24} />,
      <Tablet key="tablet" size={24} />,
      <Monitor key="desktop" size={24} />,
      <Smartphone key="app1" size={24} />,
      <Smartphone key="app2" size={24} />,
      <Monitor key="header" size={24} />
    ];
    
    // Responsive sizing - Mobile: volle Höhe, Desktop: Quadrat (gleich hoch wie breit)
    // Berechne optimale Größe basierend auf Viewport mit min/max Constraints
    const minMockupSize = 400; // Mindestgröße für sehr breite Screens
    const maxMockupSize = 600; // Maximalgröße
    const availableWidth = windowWidth - (showSidebar ? 320 : 0) - 100; // Abzüglich Sidebar und Padding
    const calculatedSize = availableWidth * 0.35;
    
    // Stelle sicher, dass die Größe zwischen min und max liegt
    const mockupSize = isDesktop 
      ? Math.max(minMockupSize, Math.min(maxMockupSize, calculatedSize))
      : 'auto';
    
    const imageWidth = isDesktop ? `${mockupSize}px` : windowWidth < 576 ? '300px' : '450px';
    const imageHeight = isDesktop ? `${mockupSize}px` : 'auto'; // Desktop: Quadrat
    const containerWidth = isDesktop ? `${mockupSize}px` : windowWidth < 576 ? '150px' : '225px';
    const containerHeight = isDesktop ? `${mockupSize}px` : 'auto'; // Desktop: Quadrat
    
    // Maximum height for mockup container on desktop to prevent oversized images on ultra-wide screens
    const maxContainerHeight = isDesktop ? '900px' : 'none';
    
    return (
      <div 
        ref={mockupRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isDesktop ? '20px' : '16px',
          alignItems: 'center',
          justifyContent: isDesktop ? 'center' : 'flex-start',
          width: '100%',
          maxWidth: isDesktop ? '100%' : '100%',
          height: isDesktop ? '100vh' : 'auto',
          maxHeight: maxContainerHeight,
          margin: '0 auto',
          pointerEvents: 'none',
        }}
      >
        {/* Current mockup - simplified */}
        <div
          style={{
            position: 'relative',
            width: containerWidth,
            height: containerHeight,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            zIndex:0,
            justifyContent: 'center',
            transition: 'opacity 0.5s ease',
          }}
        >
          {/* Image container with shadow and border - Quadrat auf Desktop */}
          <div style={{
            width: imageWidth,
            height: imageHeight, // Desktop: 600px für Quadrat
            maxHeight: isDesktop ? imageHeight : 'none',
            borderRadius: isDesktop ? '20px' : '12px',
            overflow: 'hidden',
            zIndex:0,
            boxShadow: isDesktop ? '0 15px 35px rgba(0, 0, 0, 0.25)' : '0 8px 20px rgba(0, 0, 0, 0.2)',
            border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            position: 'relative',
            animation: initialLoad && isDesktop ? 'floatUp 6s ease-in-out infinite' : 'none',
          }}>
            <img 
              src={MOCKUP_IMAGES[currentMockupIndex]}
              alt={`Device mockup ${currentMockupIndex + 1}`}
              style={{
                width: '100%',
                height: isDesktop ? '100%' : 'auto',
                maxHeight: isDesktop ? '100%' : 'none',
                zIndex:0,
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          </div>
          
          {/* Caption below image */}
          <div style={{
            marginTop: isDesktop ? '20px' : '12px',
            textAlign: 'center',
            maxWidth: '350px',
            padding: isDesktop ? '0' : '0 10px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}>
              {React.cloneElement(mockupIcons[currentMockupIndex], { 
                size: isDesktop ? 24 : 18 
              })}
              <h3 style={{
                margin: 0,
                fontSize: isDesktop ? '16px' : '14px',
                fontWeight: '600',
                color: darkMode ? '#fff' : '#1f2937',
              }}>
                {MOCKUP_DESCRIPTIONS[currentMockupIndex]}
              </h3>
            </div>
            <p style={{
              margin: '8px 0 0',
              fontSize: isDesktop ? '13px' : '12px',
              color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
            }}>
              Responsive design for every device
            </p>
            
            {/* Privacy Notice - sehr kompakt */}
            <div style={{
              marginTop: isDesktop ? '16px' : '12px',
              padding: isDesktop ? '8px 12px' : '6px 10px',
              background: 'rgba(52, 199, 89, 0.06)',
              borderRadius: isDesktop ? '8px' : '6px',
              border: '1px solid rgba(52, 199, 89, 0.12)',
              display: 'flex',
              alignItems: 'center',
              gap: isDesktop ? '8px' : '6px',
              textAlign: 'left',
            }}>
              <div style={{
                width: isDesktop ? '22px' : '18px',
                height: isDesktop ? '22px' : '18px',
                flexShrink: 0,
                borderRadius: '50%',
                background: 'rgba(52, 199, 89, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Lock size={isDesktop ? 11 : 9} color="#34C759" />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{
                  fontSize: isDesktop ? '10px' : '9px',
                  fontWeight: '600',
                  color: darkMode ? '#C8E6D7' : '#2E7B4B',
                  margin: '0 0 2px',
                }}>
                  Anonymous Feedback
                </p>
                <p style={{
                  fontSize: isDesktop ? '9px' : '8px',
                  color: darkMode ? 'rgba(200, 230, 215, 0.6)' : 'rgba(46, 123, 75, 0.6)',
                  margin: 0,
                  lineHeight: 1.2,
                }}>
                  Your responses are completely private
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Dots indicator */}
        <div style={{
          display: 'flex',
          gap: isDesktop ? '8px' : '6px',
          justifyContent: 'center',
        }}>
          {MOCKUP_IMAGES.map((_, index) => (
            <div 
              key={index}
              style={{
                width: isDesktop ? '10px' : '8px',
                height: isDesktop ? '10px' : '8px',
                borderRadius: '50%',
                background: currentMockupIndex === index 
                  ? 'rgba(255, 59, 48, 0.8)' 
                  : darkMode 
                    ? 'rgba(255, 255, 255, 0.3)' 
                    : 'rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
              }}
            ></div>
          ))}
        </div>
      </div>
    );
  };
  
  // Privacy Policy modal
  const PrivacyPolicyModal = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(5px)',
      zIndex: 2000,
      display: privacyOpen ? 'flex' : 'none',
      justifyContent: 'center',
      alignItems: 'center',
      animation: 'fadeIn 0.3s ease',
      overflow: 'auto'
    }}>
      <div style={{
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        background: darkMode ? 'rgba(28, 28, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(20px)',
        border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
      }}>
        {/* Modal Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
        }}>
          <h3 style={{
            margin: 0,
            color: darkMode ? '#fff' : '#000',
            fontSize: '20px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Shield size={24} style={{ color: '#FF3B30' }} />
            Privacy Notice
          </h3>
          <button 
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onClick={() => setPrivacyOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Content - Scrollable */}
        <div style={{
          padding: '20px',
          maxHeight: 'calc(90vh - 150px)',
          overflowY: 'auto',
          color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          lineHeight: 1.6,
          fontSize: '14px',
        }}>
          <h4 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: darkMode ? '#fff' : '#000',
            marginTop: '10px',
            marginBottom: '16px',
          }}>Anonymous Feedback Collection</h4>
          
          <p>Our feedback system is designed to collect honest opinions while protecting your privacy:</p>
          
          <ul style={{ paddingLeft: '24px', marginBottom: '20px' }}>
            <li style={{ marginBottom: '10px' }}>
              <strong>Anonymous Submissions:</strong> We do not collect any personally identifiable information (PII) such as your name, email, IP address, or any account details with your feedback.
            </li>
            <li style={{ marginBottom: '10px' }}>
              <strong>Secure Processing:</strong> Your responses are securely transmitted and stored without any connection to your identity.
            </li>
            <li style={{ marginBottom: '10px' }}>
              <strong>No Tracking:</strong> We don't use cookies or tracking technologies to identify who submitted which feedback.
            </li>
            <li style={{ marginBottom: '10px' }}>
              <strong>Aggregate Analysis:</strong> Responses are analyzed in aggregate to improve our services, without attempt to identify individual respondents.
            </li>
          </ul>
          
          <h4 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: darkMode ? '#fff' : '#000',
            marginTop: '24px',
            marginBottom: '16px',
          }}>How We Use Feedback</h4>
          
          <p>Your feedback helps us improve. We use the collected information to:</p>
          
          <ul style={{ paddingLeft: '24px', marginBottom: '20px' }}>
            <li style={{ marginBottom: '10px' }}>Understand user experiences and satisfaction levels</li>
            <li style={{ marginBottom: '10px' }}>Identify areas for improvement in our services</li>
            <li style={{ marginBottom: '10px' }}>Guide future development decisions</li>
          </ul>
          
          <div style={{
            padding: '15px',
            background: darkMode ? 'rgba(52, 199, 89, 0.1)' : 'rgba(52, 199, 89, 0.05)',
            borderRadius: '12px',
            border: '1px solid rgba(52, 199, 89, 0.2)',
            marginTop: '20px',
            marginBottom: '20px',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '10px',
            }}>
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'rgba(52, 199, 89, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CheckCircle size={16} color="#34C759" />
              </div>
              <strong style={{ color: darkMode ? '#fff' : '#000' }}>Your Privacy is Our Priority</strong>
            </div>
            <p style={{ margin: 0, fontSize: '13px' }}>
              We value your honest feedback and protect your privacy. No submission can be traced back to you.
            </p>
          </div>
          
          <p style={{ fontSize: '13px', color: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)' }}>
            Last updated: April 2025
          </p>
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <button 
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
              border: 'none',
              borderRadius: '12px',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onClick={() => setPrivacyOpen(false)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 59, 48, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
  
  // Define theme
  const theme = {
    colors: {
      primary: '#FF3B30',
      primaryHover: '#E62E26',
      secondary: '#FF2D55',
      background: darkMode ? '#121212' : '#F8F9FA',
      backgroundAlt: darkMode ? '#1A1A1A' : '#F2F2F2',
      card: darkMode ? '#1E1E1E' : '#FFFFFF',
      cardAlt: darkMode ? '#252525' : '#F9F9F9',
      text: darkMode ? '#FFFFFF' : '#1A1A1A',
      textSecondary: darkMode ? '#AAAAAA' : '#6F6F6F',
      textTertiary: darkMode ? '#777777' : '#A0A0A0',
      border: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      borderStrong: darkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
      inputBg: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      success: '#34C759',
      error: '#FF3B30',
      warning: '#FFCC00',
      info: '#007AFF',
    },
    shadows: {
      small: darkMode ? 'none' : '0 2px 5px rgba(0, 0, 0, 0.1)',
      medium: darkMode ? '0 4px 15px rgba(0, 0, 0, 0.3)' : '0 4px 15px rgba(0, 0, 0, 0.1)',
      large: darkMode ? '0 10px 30px rgba(0, 0, 0, 0.4)' : '0 10px 30px rgba(0, 0, 0, 0.15)',
      button: darkMode ? '0 4px 10px rgba(255, 59, 48, 0.25)' : '0 4px 10px rgba(255, 59, 48, 0.2)',
      buttonHover: darkMode ? '0 6px 15px rgba(255, 59, 48, 0.35)' : '0 6px 15px rgba(255, 59, 48, 0.3)',
    },
    gradients: {
      primary: 'linear-gradient(to right, #FF3B30, #FF2D55)',
      primarySoft: darkMode ?
        'linear-gradient(to right, rgba(255, 59, 48, 0.2), rgba(255, 45, 85, 0.2))' :
        'linear-gradient(to right, rgba(255, 59, 48, 0.1), rgba(255, 45, 85, 0.1))',
      card: darkMode ?
        'linear-gradient(to bottom right, rgba(30, 30, 30, 0.7), rgba(25, 25, 25, 0.8))' :
        'linear-gradient(to bottom right, rgba(255, 255, 255, 0.9), rgba(250, 250, 250, 0.95))',
      sidebar: darkMode ?
        'linear-gradient(to bottom, rgba(35, 35, 35, 0.9), rgba(25, 25, 25, 0.95))' :
        'linear-gradient(to bottom, rgba(255, 255, 255, 0.97), rgba(248, 248, 248, 0.99))',
      header: darkMode ?
        'linear-gradient(to right, rgba(30, 30, 30, 0.95), rgba(25, 25, 25, 0.95))' :
        'linear-gradient(to right, rgba(255, 255, 255, 0.95), rgba(250, 250, 250, 0.95))',
    },
    transitions: {
      fast: 'all 0.2s ease',
      medium: 'all 0.3s ease',
      slow: 'all 0.5s ease',
      bounce: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    borderRadius: {
      small: '6px',
      medium: '12px',
      large: '16px',
      extraLarge: '24px',
      pill: '9999px',
    }
  };
  
  // Already submitted message
  const AlreadySubmittedMessage = () => (
    <div style={{
      maxWidth: isDesktop ? '800px' : '100%',
      margin: '0 auto',
      background: theme.colors.card,
      borderRadius: theme.borderRadius.large,
      boxShadow: theme.shadows.medium,
      padding: '40px',
      zIndex:99,
      textAlign: 'center',
      animation: 'scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    }}>
      <div style={{
        width: '100px',
        height: '100px',
        margin: '0 auto 30px',
        background: 'rgba(0, 122, 255, 0.1)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 0 0 15px rgba(0, 122, 255, 0.05)',
      }}>
        <Info size={50} color="#007AFF" />
      </div>
      
      <h2 style={{
        fontSize: '28px',
        fontWeight: '700',
        color: theme.colors.text,
        marginBottom: '16px',
      }}>
        Thank You for Your Previous Feedback!
      </h2>
      
      <p style={{
        fontSize: '16px',
        color: theme.colors.textSecondary,
        maxWidth: '500px',
        margin: '0 auto 30px',
        lineHeight: 1.6,
      }}>
        Our records show you've already submitted feedback for this session.
        We limit submissions to one per person to ensure balanced responses.
      </p>
      
      <p style={{
        fontSize: '14px',
        color: theme.colors.textTertiary,
        maxWidth: '500px',
        margin: '0 auto 30px',
        lineHeight: 1.6,
      }}>
        If you need to provide additional feedback, please contact support.
      </p>
    </div>
  );
  
  // Success view after submitting feedback
  const SuccessView = () => (
    <div style={{
      maxWidth: isDesktop ? '800px' : '100%',
      margin: '0 auto',
      background: theme.colors.card,
      borderRadius: theme.borderRadius.large,
      boxShadow: theme.shadows.medium,
      padding: '40px',
      textAlign: 'center',
      animation: 'scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    }}>
      <div style={{
        width: '100px',
        height: '100px',
        margin: '0 auto 30px',
        background: 'rgba(52, 199, 89, 0.1)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 0 0 15px rgba(52, 199, 89, 0.05)',
        animation: 'pulse 2s infinite ease-in-out',
      }}>
        <CheckCircle size={50} color="#34C759" />
      </div>
      
      <h2 style={{
        fontSize: '28px',
        fontWeight: '700',
        color: theme.colors.text,
        marginBottom: '16px',
      }}>
        Thank You for Your Feedback!
      </h2>
      
      <p style={{
        fontSize: '16px',
        color: theme.colors.textSecondary,
        maxWidth: '500px',
        margin: '0 auto 30px',
        lineHeight: 1.6,
      }}>
        Your responses have been submitted anonymously.
        We appreciate your input as it helps us improve our services.
      </p>
    </div>
  );
  
  // Desktop/Mobile sidebar component
  const DesktopSidebar = () => (
    <div className={isDesktop ? "desktop-sidebar" : "mobile-sidebar"} style={{
      position: 'fixed',
      top: isDesktop ? '80px' : '0',
      bottom: isDesktop ? '20px' : 'auto',
      left: isDesktop ? '20px' : '0',
      right: isDesktop ? 'auto' : '0',
      width: isDesktop ? '280px' : '100%',
      maxHeight: isDesktop ? 'auto' : '80vh',
      background: theme.gradients.sidebar,
      borderRadius: isDesktop ? theme.borderRadius.large : '0 0 20px 20px',
      boxShadow: theme.shadows.medium,
      border: `1px solid ${theme.colors.border}`,
      display: 'flex',
      flexDirection: 'column',
      padding: isDesktop ? '24px 0' : '20px 0',
      overflowY: 'auto',
      animation: isDesktop 
        ? (initialLoad && showSidebar ? 'slideInLeft 0.5s ease' : 'none')
        : (showSidebar ? 'slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)' : 'slideUp 0.3s ease'),
      zIndex: 1000,
      backdropFilter: 'blur(10px)',
      transform: !isDesktop && !showSidebar ? 'translateY(-100%)' : 'translateY(0)',
      transition: !isDesktop ? 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
    }}>
      {/* Header - Neu hinzugefügt */}
      <div style={{
        padding: '0 24px 20px',
        borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <h2 style={{
          margin: 0,
          fontSize: '20px',
          fontWeight: 700,
          color: theme.colors.text,
        }}>
          Menü
        </h2>
        {!isDesktop && (
          <button
            onClick={() => setShowSidebar(false)}
            style={{
              background: 'transparent',
              border: 'none',
              color: theme.colors.textSecondary,
              cursor: 'pointer',
              padding: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <X size={24} />
          </button>
        )}
      </div>
      
      {/* Company branding */}
      <div style={{
        padding: '20px 24px 24px',
        borderBottom: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '15px',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: theme.borderRadius.medium,
            background: theme.gradients.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <MessageCircle size={28} color="#FFFFFF" />
          </div>
          <div>
            <h3 style={{
              margin: '0 0 2px 0',
              fontSize: '18px',
              fontWeight: 700,
              color: theme.colors.text,
            }}>
              cAmPP Feedback
            </h3>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              Help us improve
            </p>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          padding: '10px 15px',
          borderRadius: theme.borderRadius.medium,
          marginBottom: '15px',
        }}>
          <Shield size={18} color={theme.colors.textSecondary} />
          <span style={{
            fontSize: '13px',
            color: theme.colors.textSecondary,
          }}>
            All submissions are anonymous
          </span>
        </div>
      </div>
      
      {/* Navigation */}
      <div style={{
        padding: '24px',
        borderBottom: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'uppercase',
          color: theme.colors.textTertiary,
          marginBottom: '12px',
          letterSpacing: '0.5px',
        }}>
          Navigation
        </div>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 15px',
              background: activeTab === 'feedback' ? theme.gradients.primarySoft : 'transparent',
              border: 'none',
              borderRadius: theme.borderRadius.medium,
              color: activeTab === 'feedback' ? theme.colors.primary : theme.colors.textSecondary,
              fontWeight: activeTab === 'feedback' ? '600' : '400',
              fontSize: '14px',
              textAlign: 'left',
              cursor: 'pointer',
              transition: theme.transitions.medium,
            }}
            onClick={() => setActiveTab('feedback')}
          >
            <MessageCircle size={18} />
            Feedback Form
          </button>
          
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 15px',
              background: activeTab === 'info' ? theme.gradients.primarySoft : 'transparent',
              border: 'none',
              borderRadius: theme.borderRadius.medium,
              color: activeTab === 'info' ? theme.colors.primary : theme.colors.textSecondary,
              fontWeight: activeTab === 'info' ? '600' : '400',
              fontSize: '14px',
              textAlign: 'left',
              cursor: 'pointer',
              transition: theme.transitions.medium,
            }}
            onClick={() => setActiveTab('info')}
          >
            <Info size={18} />
            About Feedback
          </button>
          
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 15px',
              background: activeTab === 'help' ? theme.gradients.primarySoft : 'transparent',
              border: 'none',
              borderRadius: theme.borderRadius.medium,
              color: activeTab === 'help' ? theme.colors.primary : theme.colors.textSecondary,
              fontWeight: activeTab === 'help' ? '600' : '400',
              fontSize: '14px',
              textAlign: 'left',
              cursor: 'pointer',
              transition: theme.transitions.medium,
            }}
            onClick={() => setActiveTab('help')}
          >
            <LifeBuoy size={18} />
            Help & Support
          </button>
        </div>
      </div>
      
      {/* View Mode Controls */}
      {activeTab === 'feedback' && (
        <div style={{
          padding: '24px',
          borderBottom: `1px solid ${theme.colors.border}`,
        }}>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            textTransform: 'uppercase',
            color: theme.colors.textTertiary,
            marginBottom: '12px',
            letterSpacing: '0.5px',
          }}>
            View Mode
          </div>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 15px',
                background: viewMode === 'standard' ? theme.gradients.primarySoft : 'transparent',
                border: 'none',
                borderRadius: theme.borderRadius.medium,
                color: viewMode === 'standard' ? theme.colors.primary : theme.colors.textSecondary,
                fontWeight: viewMode === 'standard' ? '600' : '400',
                fontSize: '14px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: theme.transitions.medium,
              }}
              onClick={() => changeViewMode('standard')}
            >
              <Layout size={18} />
              Standard View
            </button>
            
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 15px',
                background: viewMode === 'step' ? theme.gradients.primarySoft : 'transparent',
                border: 'none',
                borderRadius: theme.borderRadius.medium,
                color: viewMode === 'step' ? theme.colors.primary : theme.colors.textSecondary,
                fontWeight: viewMode === 'step' ? '600' : '400',
                fontSize: '14px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: theme.transitions.medium,
              }}
              onClick={() => changeViewMode('step')}
            >
              <ArrowRight size={18} />
              Step by Step
            </button>
            
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 15px',
                background: viewMode === 'compact' ? theme.gradients.primarySoft : 'transparent',
                border: 'none',
                borderRadius: theme.borderRadius.medium,
                color: viewMode === 'compact' ? theme.colors.primary : theme.colors.textSecondary,
                fontWeight: viewMode === 'compact' ? '600' : '400',
                fontSize: '14px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: theme.transitions.medium,
              }}
              onClick={() => changeViewMode('compact')}
            >
              <Archive size={18} />
              Compact View
            </button>
          </div>
        </div>
      )}
      
      {/* Settings and theme toggle */}
      <div style={{
        padding: '24px',
        borderBottom: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'uppercase',
          color: theme.colors.textTertiary,
          marginBottom: '12px',
          letterSpacing: '0.5px',
        }}>
          Settings
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 15px',
          background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          borderRadius: theme.borderRadius.medium,
          marginBottom: '10px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}>
            {darkMode ? <Moon size={18} color={theme.colors.textSecondary} /> : <Sun size={18} color={theme.colors.textSecondary} />}
            <span style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              {darkMode ? 'Dark Mode' : 'Light Mode'}
            </span>
          </div>
          
          <div 
            style={{
              width: '40px',
              height: '22px',
              borderRadius: '11px',
              background: darkMode ? 'rgba(255, 59, 48, 0.2)' : 'rgba(0, 0, 0, 0.1)',
              position: 'relative',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onClick={toggleDarkMode}
          >
            <div style={{
              width: '18px',
              height: '18px',
              borderRadius: '9px',
              background: darkMode ? '#FF3B30' : '#FFFFFF',
              position: 'absolute',
              top: '2px',
              left: darkMode ? '20px' : '2px',
              transition: 'left 0.2s, background-color 0.2s',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
            }}></div>
          </div>
        </div>
        
        {/* Privacy policy */}
        <button
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            padding: '12px 15px',
            background: 'transparent',
            border: 'none',
            borderRadius: theme.borderRadius.medium,
            color: theme.colors.textSecondary,
            fontSize: '14px',
            textAlign: 'left',
            cursor: 'pointer',
            transition: theme.transitions.medium,
          }}
          onClick={() => setPrivacyOpen(true)}
        >
          <Shield size={18} />
          Privacy Policy
        </button>
      </div>
      
      {/* Progress information */}
      {activeTab === 'feedback' && questions.length > 0 && (
        <div style={{
          padding: '24px',
          marginTop: 'auto',
        }}>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            textTransform: 'uppercase',
            color: theme.colors.textTertiary,
            marginBottom: '12px',
            letterSpacing: '0.5px',
          }}>
            Your Progress
          </div>
          
          <div style={{
            marginBottom: '10px',
          }}>
            <ProgressBar percentage={completionPercentage} />
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              <span>{completionPercentage}% Complete</span>
              <span>{responses.filter(r => 
                (r.starRating !== null && r.starRating !== undefined) || 
                (r.textResponse && r.textResponse.trim() !== '') ||
                r.skipped === true
              ).length} of {questions.length}</span>
            </div>
          </div>
          
          {completionPercentage === 100 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 15px',
              background: 'rgba(52, 199, 89, 0.1)',
              borderRadius: theme.borderRadius.medium,
              border: '1px solid rgba(52, 199, 89, 0.2)',
              marginTop: '15px',
            }}>
              <CheckCircle size={18} color="#34C759" />
              <span style={{
                fontSize: '14px',
                color: '#34C759',
                fontWeight: '500',
              }}>
                Ready to submit!
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
  
  // Header component
  const Header = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      
      height: '64px',
      background: theme.gradients.header,
      backdropFilter: 'blur(10px)',
      zIndex: 100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 20px',
      borderBottom: `1px solid ${theme.colors.border}`,
      boxShadow: theme.shadows.small,
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        {/* Mobile Menu Button */}
        {!isDesktop && (
          <button
            style={{
              width: '40px',
              height: '40px',
              borderRadius: theme.borderRadius.medium,
              background: theme.colors.inputBg,
              border: `1px solid ${theme.colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.colors.textSecondary,
              cursor: 'pointer',
              transition: theme.transitions.medium,
            }}
            onClick={() => setShowSidebar(!showSidebar)}
            title="Toggle Menu"
          >
            <Layout size={20} />
          </button>
        )}
        
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: theme.borderRadius.medium,
          background: theme.gradients.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <MessageCircle size={22} color="#FFFFFF" />
        </div>
        
        <div style={{
          fontSize: windowWidth < 576 ? '16px' : '18px',
          fontWeight: '700',
          background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          letterSpacing: '-0.5px',
        }}>
          cAmPP Feedback
        </div>
      </div>
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        {isDesktop && (
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 15px',
              background: theme.colors.inputBg,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.medium,
              color: theme.colors.textSecondary,
              fontSize: '14px',
              cursor: 'pointer',
              transition: theme.transitions.medium,
            }}
            onClick={() => setShowSidebar(!showSidebar)}
          >
            {showSidebar ? 'Hide Sidebar' : 'Show Sidebar'}
          </button>
        )}
        
        <button
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: theme.colors.inputBg,
            border: `1px solid ${theme.colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.colors.textSecondary,
            cursor: 'pointer',
            transition: theme.transitions.medium,
          }}
          onClick={toggleDarkMode}
          title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {darkMode ? <Sun size={20} /> : <Moon size={20} />}
        </button>
        
        {onClose && (
          <button
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: theme.colors.error,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              cursor: 'pointer',
              transition: theme.transitions.medium,
            }}
            onClick={onClose}
            title="Close"
          >
            <X size={20} />
          </button>
        )}
      </div>
    </div>
  );
  
  // Skip Question Button component
  const SkipQuestionButton = ({ onClick, disabled = false }) => (
    <button
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 15px',
        background: disabled ? 'rgba(150, 150, 150, 0.1)' : 'transparent',
        border: `1px solid ${disabled ? 'rgba(150, 150, 150, 0.2)' : theme.colors.border}`,
        borderRadius: theme.borderRadius.medium,
        color: disabled ? 'rgba(150, 150, 150, 0.5)' : theme.colors.textSecondary,
        fontSize: '14px',
        fontWeight: '500',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: theme.transitions.medium,
      }}
      onClick={onClick}
      disabled={disabled}
      title={disabled ? "Already skipped" : "Skip this question"}
    >
      <SkipForward size={16} />
      Skip Question
    </button>
  );
  
  // About Feedback tab content
  const AboutFeedbackTab = () => (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      zIndex:999,

    }}>
      <div style={{
        background: theme.colors.card,
        borderRadius: theme.borderRadius.large,
        boxShadow: theme.shadows.medium,
        padding: '30px',
        zIndex:999,

        marginBottom: '30px',
        border: `1px solid ${theme.colors.border}`,
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: theme.colors.text,
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <Info size={24} style={{ color: theme.colors.primary }} />
          About Our Feedback System
        </h2>
        
        <p style={{
          fontSize: '16px',
          color: theme.colors.textSecondary,
          lineHeight: 1.6,
          marginBottom: '20px',
        }}>
          Your feedback is essential to our continuous improvement. We've designed this feedback system to gather honest opinions while ensuring complete anonymity.
        </p>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : '1fr',
          gap: '20px',
          marginBottom: '30px',
        }}>
          <div style={{
            background: theme.colors.cardAlt,
            padding: '20px',
            borderRadius: theme.borderRadius.medium,
            border: `1px solid ${theme.colors.border}`,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '15px',
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'rgba(255, 59, 48, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Shield size={20} color="#FF3B30" />
              </div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: theme.colors.text,
                margin: 0,
              }}>
                Anonymous Submissions
              </h3>
            </div>
            
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              lineHeight: 1.5,
              margin: 0,
            }}>
              We don't collect any personally identifiable information. Your feedback cannot be traced back to you.
            </p>
          </div>
          
          <div style={{
            background: theme.colors.cardAlt,
            padding: '20px',
            borderRadius: theme.borderRadius.medium,
            border: `1px solid ${theme.colors.border}`,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '15px',
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'rgba(255, 59, 48, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <ThumbsUp size={20} color="#FF3B30" />
              </div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: theme.colors.text,
                margin: 0,
              }}>
                Honest Responses
              </h3>
            </div>
            
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              lineHeight: 1.5,
              margin: 0,
            }}>
              We encourage candid feedback to help us understand what's working and what needs improvement.
            </p>
          </div>
          
          <div style={{
            background: theme.colors.cardAlt,
            padding: '20px',
            borderRadius: theme.borderRadius.medium,
            border: `1px solid ${theme.colors.border}`,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '15px',
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'rgba(255, 59, 48, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <BarChart2 size={20} color="#FF3B30" />
              </div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: theme.colors.text,
                margin: 0,
              }}>
                Data Analysis
              </h3>
            </div>
            
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              lineHeight: 1.5,
              margin: 0,
            }}>
              We analyze responses in aggregate to identify trends and make informed decisions.
            </p>
          </div>
          
          <div style={{
            background: theme.colors.cardAlt,
            padding: '20px',
            borderRadius: theme.borderRadius.medium,
            border: `1px solid ${theme.colors.border}`,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '15px',
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'rgba(255, 59, 48, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Award size={20} color="#FF3B30" />
              </div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: theme.colors.text,
                margin: 0,
              }}>
                Continuous Improvement
              </h3>
            </div>
            
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              lineHeight: 1.5,
              margin: 0,
            }}>
              Your input directly influences our future developments and service improvements.
            </p>
          </div>
        </div>
      </div>
      
      <div style={{
        background: theme.colors.card,
        borderRadius: theme.borderRadius.large,
        boxShadow: theme.shadows.medium,
        padding: '30px',
        marginBottom: '30px',
        border: `1px solid ${theme.colors.border}`,
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: theme.colors.text,
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <Calendar size={24} style={{ color: theme.colors.primary }} />
          Feedback Collection Timeline
        </h2>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          marginBottom: '20px',
        }}>
          <div style={{
            display: 'flex',
            gap: '15px',
            alignItems: 'flex-start',
          }}>
            <div style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: theme.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '600',
              flexShrink: 0,
              marginTop: '3px',
            }}>
              1
            </div>
            <div>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: theme.colors.text,
                marginBottom: '5px',
              }}>
                Post-Camp Evaluation
              </h3>
              <p style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                lineHeight: 1.5,
                margin: 0,
              }}>
                After each camp, we collect detailed feedback to assess the overall experience and identify areas for improvement.
              </p>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            gap: '15px',
            alignItems: 'flex-start',
          }}>
            <div style={{
              width: '30px',height: '30px',
              borderRadius: '50%',
              background: theme.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '600',
              flexShrink: 0,
              marginTop: '3px',
            }}>
              2
            </div>
            <div>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: theme.colors.text,
                marginBottom: '5px',
              }}>
                Analysis and Planning
              </h3>
              <p style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                lineHeight: 1.5,
                margin: 0,
              }}>
                We thoroughly analyze the collected feedback to identify patterns and areas for improvement, then create an action plan.
              </p>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            gap: '15px',
            alignItems: 'flex-start',
          }}>
            <div style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: theme.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '600',
              flexShrink: 0,
              marginTop: '3px',
            }}>
              3
            </div>
            <div>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: theme.colors.text,
                marginBottom: '5px',
              }}>
                Implementation
              </h3>
              <p style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                lineHeight: 1.5,
                margin: 0,
              }}>
                We implement changes based on feedback, enhancing our camp experiences and services for future participants.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div style={{
        background: theme.colors.card,
        borderRadius: theme.borderRadius.large,
        boxShadow: theme.shadows.medium,
        padding: '30px',
        marginBottom: '30px',
        border: `1px solid ${theme.colors.border}`,
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: theme.colors.text,
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <Heart size={24} style={{ color: theme.colors.primary }} />
          Your Impact
        </h2>
        
        <p style={{
          fontSize: '16px',
          color: theme.colors.textSecondary,
          lineHeight: 1.6,
          marginBottom: '20px',
        }}>
          By providing your feedback, you're directly contributing to:
        </p>
        
        <ul style={{
          fontSize: '16px',
          color: theme.colors.textSecondary,
          lineHeight: 1.6,
          paddingLeft: '25px',
        }}>
          <li style={{ marginBottom: '10px' }}>Improving the camp experience for future participants</li>
          <li style={{ marginBottom: '10px' }}>Refining our program structures and activities</li>
          <li style={{ marginBottom: '10px' }}>Enhancing support services and resources</li>
          <li style={{ marginBottom: '10px' }}>Creating more meaningful and engaging camp environments</li>
        </ul>
      </div>
    </div>
  );
  
  // Help & Support tab content
  const HelpSupportTab = () => (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
    }}>
      <div style={{
        background: theme.colors.card,
        borderRadius: theme.borderRadius.large,boxShadow: theme.shadows.medium,
        padding: '30px',
        marginBottom: '30px',
        border: `1px solid ${theme.colors.border}`,
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: theme.colors.text,
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <LifeBuoy size={24} style={{ color: theme.colors.primary }} />
          Feedback Form Help
        </h2>
        
        <p style={{
          fontSize: '16px',
          color: theme.colors.textSecondary,
          lineHeight: 1.6,
          marginBottom: '20px',
        }}>
          This guide will help you understand how to use our feedback form effectively.
        </p>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}>
          <div style={{
            background: theme.colors.cardAlt,
            padding: '20px',
            borderRadius: theme.borderRadius.medium,
            border: `1px solid ${theme.colors.border}`,
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: theme.colors.text,
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <Star size={20} style={{ color: theme.colors.primary }} />
              Rating Questions
            </h3>
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              lineHeight: 1.5,
              margin: 0,
            }}>
              For rating questions, click on the desired number of stars to indicate your rating. Hover over the stars to preview your selection before clicking.
            </p>
          </div>
          
          <div style={{
            background: theme.colors.cardAlt,
            padding: '20px',
            borderRadius: theme.borderRadius.medium,
            border: `1px solid ${theme.colors.border}`,
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: theme.colors.text,
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <MessageCircle size={20} style={{ color: theme.colors.primary }} />
              Text Responses
            </h3>
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              lineHeight: 1.5,
              margin: 0,
            }}>
              For open-ended questions, provide detailed feedback in the text area. The textarea will automatically expand as you type. Be as specific and honest as possible.
            </p>
          </div>
          
          <div style={{
            background: theme.colors.cardAlt,
            padding: '20px',
            borderRadius: theme.borderRadius.medium,
            border: `1px solid ${theme.colors.border}`,
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: theme.colors.text,
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <SkipForward size={20} style={{ color: theme.colors.primary }} />
              Skipping Questions
            </h3>
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              lineHeight: 1.5,
              margin: 0,
            }}>
              If you don't want to answer a specific question, you can use the "Skip Question" button. Skipped questions won't be included in your submission, and you can still submit the form.
            </p>
          </div>
          
          <div style={{
            background: theme.colors.cardAlt,
            padding: '20px',
            borderRadius: theme.borderRadius.medium,
            border: `1px solid ${theme.colors.border}`,
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: theme.colors.text,
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <Layout size={20} style={{ color: theme.colors.primary }} />
              View Modes
            </h3>
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              lineHeight: 1.5,
              margin: '0 0 15px 0',
            }}>
              The sidebar offers different view modes for completing the feedback:
            </p>
            <ul style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              lineHeight: 1.5,
              paddingLeft: '20px',
              margin: 0,
            }}>
              <li style={{ marginBottom: '8px' }}><strong>Standard View:</strong> Shows all questions at once.</li>
              <li style={{ marginBottom: '8px' }}><strong>Step by Step:</strong> Shows one question at a time with navigation buttons.</li>
              <li><strong>Compact View:</strong> Displays questions in a more condensed format.</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div style={{
        background: theme.colors.card,
        borderRadius: theme.borderRadius.large,
        boxShadow: theme.shadows.medium,
        padding: '30px',
        marginBottom: '30px',
        border: `1px solid ${theme.colors.border}`,
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: theme.colors.text,
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <Mail size={24} style={{ color: theme.colors.primary }} />
          Contact Us
        </h2>
        
        <p style={{
          fontSize: '16px',
          color: theme.colors.textSecondary,
          lineHeight: 1.6,
          marginBottom: '20px',
        }}>
          If you're experiencing any issues with the feedback form or have questions about the feedback process, please contact us:
        </p>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            padding: '15px',
            background: theme.colors.cardAlt,
            borderRadius: theme.borderRadius.medium,
            border: `1px solid ${theme.colors.border}`,
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 59, 48, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Mail size={20} color="#FF3B30" />
            </div>
            <div>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: theme.colors.text,
                marginBottom: '5px',
              }}>
                Email Support
              </h3>
              <a 
                href="mailto:support@sunrise.ch"
                style={{
                  color: theme.colors.primary,
                  textDecoration: 'none',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                support@sunrise.ch
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            padding: '15px',
            background: theme.colors.cardAlt,
            borderRadius: theme.borderRadius.medium,
            border: `1px solid ${theme.colors.border}`,
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 59, 48, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Settings size={20} color="#FF3B30" />
            </div>
            <div>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: theme.colors.text,
                marginBottom: '5px',
              }}>
                Technical Support
              </h3>
              <p style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                margin: 0,
              }}>
                Available Monday-Friday, 9am-5pm CET
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  
  // Feedback form in standard view
  const StandardFeedbackForm = () => (
    <div ref={formRef} style={{
      display: 'flex',
      flexDirection: 'column',
      gap: windowWidth < 576 ? '16px' : '25px',
      marginBottom: windowWidth < 576 ? '20px' : '30px',
      padding: windowWidth < 576 ? '0 5px' : '0',
    }}>
      {questions.map((question, index) => {
        const response = responses[index];
        const isSkipped = response?.skipped === true;
        
        return (
          <div 
            ref={el => questionRefs.current[index] = el}
            key={question.id}
            className={isSkipped ? 'skipped-question' : ''}
            style={{
              padding: windowWidth < 576 ? '16px' : '25px',
              background: theme.gradients.card,
              borderRadius: windowWidth < 576 ? theme.borderRadius.medium : theme.borderRadius.large,
              boxShadow: theme.shadows.medium,
              border: `1px solid ${theme.colors.border}`,
              position: 'relative',
            }}
          >
            {isSkipped && (
              <div className="skipped-badge">
                Skipped
              </div>
            )}
            
            <h3 style={{
              fontSize: windowWidth < 576 ? '16px' : '18px',
              fontWeight: '600',
              marginBottom: windowWidth < 576 ? '15px' : '20px',
              color: theme.colors.text,
              lineHeight: 1.4,
            }}>
              {index + 1}. {question.questionText}
            </h3>
            
            {question.isStarRating ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: windowWidth < 576 ? '10px' : '15px',
              }}>
                <SunriseRating 
                  value={response?.starRating || 0}
                  onChange={(rating) => handleRatingChange(question.id, rating)}
                  size={windowWidth < 576 ? 25 : 35}
                  readOnly={isSkipped}
                />
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '13px',
                  color: theme.colors.textSecondary,
                  paddingTop: '5px',
                }}>
                  <span>Poor</span>
                  <span>Excellent</span>
                </div>
              </div>
            ) : (
              <div className="textarea-wrapper" style={{
                maxWidth: windowWidth < 768 ? '80%' : '450px',
                width: '100%',
              }}>
                <textarea 
                  key={`textarea-${question.id}`}
                  className="autosize-textarea focus-visible"
                  ref={(el) => {
                    if (el) {
                      // Store the ref
                      textareaRefs.current[question.id] = el;
                      
                      // Restore value from textValuesRef on re-mount
                      const savedValue = textValuesRef.current[question.id];
                      if (savedValue !== undefined && el.value !== savedValue) {
                        el.value = savedValue;
                        // Trigger resize
                        el.style.height = 'auto';
                        el.style.height = `${Math.max(120, el.scrollHeight)}px`;
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    minHeight: windowWidth < 576 ? '100px' : '120px', 
                    resize: 'vertical',
                    padding: windowWidth < 576 ? '12px' : '16px',
                    borderRadius: windowWidth < 576 ? '10px' : '12px',
                    background: darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.02)', 
                    border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    color: darkMode ? '#f3f4f6' : '#1f2937',
                    fontSize: windowWidth < 576 ? '14px' : '16px',
                    lineHeight: '1.6',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                  }}
                  placeholder="Your thoughts here..."
                  defaultValue={response?.textResponse || ''}
                  onChange={(e) => handleTextChange(question.id, e.target.value, e)}
                  onFocus={(e) => handleTextareaFocus(question.id, e)}
                  onBlur={(e) => handleTextareaBlur(question.id, e)}
                  disabled={isSkipped}
                />
              </div>
            )}
            
            {/* Skip question button */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              marginTop: '15px' 
            }}>
              <SkipQuestionButton 
                onClick={() => handleSkipQuestion(question.id)} 
                disabled={isSkipped}
              />
            </div>
          </div>
        );
      })}
      
      <button
        style={{
          background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
          color: '#fff',
          border: 'none',
          borderRadius: '12px',
          padding: '18px',
          fontSize: '18px',
          fontWeight: '600',
          cursor: submitting ? 'wait' : 'pointer',
          transition: 'all 0.3s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          marginTop: '15px',
          opacity: submitting ? 0.7 : 1,
          boxShadow: theme.shadows.button,
        }}
        onClick={submitFeedback}
        disabled={submitting || alreadySubmitted}
        onMouseEnter={(e) => {
          if (!submitting && !alreadySubmitted) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = theme.shadows.buttonHover;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = theme.shadows.button;
        }}
      >
        {submitting ? (
          <>
            <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
            Submitting...
          </>
        ) : alreadySubmitted ? (
          <>
            Already Submitted
            <CheckCircle size={20} />
          </>
        ) : (
          <>
            Submit Feedback
            <Send size={20} />
          </>
        )}
      </button>
    </div>
  );
  
  // Feedback form in step-by-step mode
  const StepFeedbackForm = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const currentResponse = responses[currentQuestionIndex];
    const isSkipped = currentResponse?.skipped === true;
    
    return (
      <div ref={formRef} style={{
        display: 'flex',
        flexDirection: 'column',
        gap: windowWidth < 576 ? '16px' : '25px',
        marginBottom: windowWidth < 576 ? '20px' : '30px',
      }}>
        {questions.length > 0 && (
          <>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
              flexWrap: 'wrap',
              gap: '8px',
            }}>
              <div style={{
                fontSize: windowWidth < 576 ? '13px' : '14px',
                color: theme.colors.textSecondary,
              }}>
                Question {currentQuestionIndex + 1} of {questions.length}
              </div>
              <div style={{
                fontSize: windowWidth < 576 ? '13px' : '14px',
                color: theme.colors.textSecondary,
                fontWeight: '600',
              }}>
                {completionPercentage}% Complete
              </div>
            </div>
            
            <ProgressBar percentage={(currentQuestionIndex + 1) / questions.length * 100} />
            
            <div 
              ref={el => questionRefs.current[currentQuestionIndex] = el}
              className={isSkipped ? 'skipped-question' : ''}
              style={{
                padding: windowWidth < 576 ? '16px' : '30px',
                background: theme.gradients.card,
                borderRadius: windowWidth < 576 ? theme.borderRadius.medium : theme.borderRadius.large,
                boxShadow: theme.shadows.medium,
                border: `1px solid ${theme.colors.border}`,
                position: 'relative',
              }}
            >
              {isSkipped && (
                <div className="skipped-badge" style={{ 
                  top: windowWidth < 576 ? '10px' : '20px', 
                  right: windowWidth < 576 ? '10px' : '20px' 
                }}>
                  Skipped
                </div>
              )}
              
              <h3 style={{
                fontSize: windowWidth < 576 ? '16px' : '20px',
                fontWeight: '600',
                marginBottom: windowWidth < 576 ? '15px' : '25px',
                color: theme.colors.text,
                lineHeight: 1.4,
              }}>
                {currentQuestionIndex + 1}. {currentQuestion.questionText}
              </h3>
              
              {currentQuestion.isStarRating ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: windowWidth < 576 ? '10px' : '15px',
                  padding: windowWidth < 576 ? '10px 0' : '20px 0',
                }}>
                  <SunriseRating 
                    value={currentResponse?.starRating || 0}
                    onChange={(rating) => handleRatingChange(currentQuestion.id, rating)}
                    size={windowWidth < 576 ? 28 : 40}
                    readOnly={isSkipped}
                  />
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: windowWidth < 576 ? '12px' : '14px',
                    color: theme.colors.textSecondary,
                    paddingTop: '10px',
                  }}>
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                </div>
              ) : (
                <div className="textarea-wrapper" style={{ 
                  padding: windowWidth < 576 ? '5px 0' : '10px 0', 
                  maxWidth: windowWidth < 768 ? '80%' : '450px',
                  width: '100%',
                }}>
                  <textarea 
                    key={`textarea-step-${currentQuestion.id}`}
                    className="autosize-textarea focus-visible"
                    ref={(el) => {
                      if (el) {
                        textareaRefs.current[currentQuestion.id] = el;
                        
                        // Restore value from textValuesRef on re-mount
                        const savedValue = textValuesRef.current[currentQuestion.id];
                        if (savedValue !== undefined && el.value !== savedValue) {
                          el.value = savedValue;
                          // Trigger resize
                          el.style.height = 'auto';
                          const minHeight = windowWidth < 576 ? 100 : 150;
                          el.style.height = `${Math.max(minHeight, el.scrollHeight)}px`;
                        }
                      }
                    }}
                    style={{
                      width: '100%',
                      minHeight: windowWidth < 576 ? '100px' : '150px', 
                      resize: 'vertical',
                      padding: windowWidth < 576 ? '12px' : '18px',
                      borderRadius: windowWidth < 576 ? '10px' : '12px',
                      background: darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.02)', 
                      border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                      color: darkMode ? '#f3f4f6' : '#1f2937',
                      fontSize: windowWidth < 576 ? '14px' : '16px',
                      lineHeight: '1.6',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                    }}
                    placeholder="Your thoughts here..."
                    defaultValue={currentResponse?.textResponse || ''}
                    onChange={(e) => handleTextChange(currentQuestion.id, e.target.value, e)}
                    onFocus={(e) => handleTextareaFocus(currentQuestion.id, e)}
                    onBlur={(e) => handleTextareaBlur(currentQuestion.id, e)}
                    disabled={isSkipped}
                  />
                </div>
              )}
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: windowWidth < 576 ? '8px' : '15px',
              marginTop: windowWidth < 576 ? '10px' : '15px',
              flexWrap: windowWidth < 576 ? 'wrap' : 'nowrap',
            }}>
              <button
                style={{
                  padding: windowWidth < 576 ? '12px' : '14px',
                  fontSize: windowWidth < 576 ? '14px' : '16px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: windowWidth < 576 ? '10px' : '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  color: theme.colors.textSecondary,
                  background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${theme.colors.border}`,
                  transition: theme.transitions.medium,
                  opacity: currentQuestionIndex === 0 ? 0.5 : 1,
                  pointerEvents: currentQuestionIndex === 0 ? 'none' : 'auto',
                  flex: windowWidth < 576 ? '1' : 'none',
                  minWidth: windowWidth < 576 ? '90px' : '120px',
                }}
                onClick={() => navigateQuestion('prev')}
                disabled={currentQuestionIndex === 0}
                onMouseEnter={(e) => {
                  if (currentQuestionIndex !== 0) {
                    e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
                }}
              >
                <ArrowLeft size={windowWidth < 576 ? 16 : 18} />
                {windowWidth < 576 ? 'Prev' : 'Previous'}
              </button>
              
              {/* Skip button */}
              <SkipQuestionButton 
                onClick={() => handleSkipQuestion(currentQuestion.id)} 
                disabled={isSkipped}
              />
              
              {currentQuestionIndex < questions.length - 1 ? (
                <button
                  style={{
                    padding: windowWidth < 576 ? '12px' : '14px',
                    fontSize: windowWidth < 576 ? '14px' : '16px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: windowWidth < 576 ? '10px' : '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    color: '#fff',
                    background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
                    transition: theme.transitions.medium,
                    boxShadow: theme.shadows.button,
                    flex: windowWidth < 576 ? '1' : 'none',
                    minWidth: windowWidth < 576 ? '90px' : '120px',
                  }}
                  onClick={() => navigateQuestion('next')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = theme.shadows.buttonHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = theme.shadows.button;
                  }}
                >
                  Next
                  <ArrowRight size={windowWidth < 576 ? 16 : 18} />
                </button>
              ) : (
                <button
                  style={{
                    padding: windowWidth < 576 ? '12px' : '14px',
                    fontSize: windowWidth < 576 ? '14px' : '16px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: windowWidth < 576 ? '10px' : '12px',
                    cursor: submitting || alreadySubmitted ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    color: '#fff',
                    background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
                    transition: theme.transitions.medium,
                    opacity: submitting || alreadySubmitted ? 0.7 : 1,
                    boxShadow: theme.shadows.button,
                    flex: windowWidth < 576 ? '1' : 'none',
                    minWidth: windowWidth < 576 ? '90px' : '120px',
                  }}
                  onClick={submitFeedback}
                  disabled={submitting || alreadySubmitted}
                  onMouseEnter={(e) => {
                    if (!submitting && !alreadySubmitted) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = theme.shadows.buttonHover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = theme.shadows.button;
                  }}
                >
                  {submitting ? (
                    <>
                      <Loader size={windowWidth < 576 ? 16 : 18} style={{ animation: 'spin 1s linear infinite' }} />
                      {windowWidth < 576 ? '...' : 'Submitting...'}
                    </>
                  ) : alreadySubmitted ? (
                    <>
                      {windowWidth < 576 ? 'Done' : 'Submitted'}
                      <CheckCircle size={windowWidth < 576 ? 16 : 18} />
                    </>
                  ) : (
                    <>
                      Submit
                      <Send size={18} />
                    </>
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  };
  
  // Feedback form in compact view
  const CompactFeedbackForm = () => (
    <div ref={formRef} style={{
      display: 'flex',
      flexDirection: 'column',
      gap: windowWidth < 576 ? '10px' : '15px',
      marginBottom: windowWidth < 576 ? '20px' : '30px',
    }}>
      <div style={{
        background: theme.gradients.card,
        borderRadius: windowWidth < 576 ? theme.borderRadius.medium : theme.borderRadius.large,
        boxShadow: theme.shadows.medium,
        border: `1px solid ${theme.colors.border}`,
        overflow: 'hidden',
      }}>
        {questions.map((question, index) => {
          const response = responses[index];
          const isSkipped = response?.skipped === true;
          
          return (
            <div 
              ref={el => questionRefs.current[index] = el}
              key={question.id}
              className={isSkipped ? 'skipped-question' : ''}
              style={{
                padding: windowWidth < 576 ? '14px' : '20px',
                borderBottom: index !== questions.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                position: 'relative',
              }}
            >
              {isSkipped && (
                <div className="skipped-badge" style={{ 
                  top: windowWidth < 576 ? '8px' : '10px', 
                  right: windowWidth < 576 ? '8px' : '10px', 
                  fontSize: windowWidth < 576 ? '10px' : '11px',
                  padding: '2px 6px' 
                }}>
                  Skipped
                </div>
              )}
              
              <h3 style={{
                fontSize: windowWidth < 576 ? '14px' : '16px',
                fontWeight: '600',
                marginBottom: windowWidth < 576 ? '12px' : '15px',
                color: theme.colors.text,
                paddingRight: isSkipped ? '60px' : '0',
                lineHeight: 1.4,
              }}>
                {index + 1}. {question.questionText}
              </h3>
              
              {question.isStarRating ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: windowWidth < 576 ? '8px' : '10px',
                }}>
                  <SunriseRating 
                    value={response?.starRating || 0}
                    onChange={(rating) => handleRatingChange(question.id, rating)}
                    size={windowWidth < 576 ? 24 : 30}
                    readOnly={isSkipped}
                  />
                </div>
              ) : (
                <div className="textarea-wrapper" style={{
                  maxWidth: windowWidth < 768 ? '80%' : '450px',
                  width: '100%',
                }}>
                  <textarea 
                    key={`textarea-compact-${question.id}`}
                    className="autosize-textarea focus-visible"
                    ref={(el) => {
                      if (el) {
                        textareaRefs.current[question.id] = el;
                        
                        // Restore value from textValuesRef on re-mount
                        const savedValue = textValuesRef.current[question.id];
                        if (savedValue !== undefined && el.value !== savedValue) {
                          el.value = savedValue;
                          // Trigger resize
                          el.style.height = 'auto';
                          const minHeight = windowWidth < 576 ? 80 : 100;
                          el.style.height = `${Math.max(minHeight, el.scrollHeight)}px`;
                        }
                      }
                    }}
                    style={{
                      width: '100%',
                      minHeight: windowWidth < 576 ? '80px' : '100px', 
                      resize: 'vertical',
                      padding: windowWidth < 576 ? '10px' : '12px',
                      borderRadius: windowWidth < 576 ? '8px' : '10px',
                      background: darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.02)', 
                      border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                      color: darkMode ? '#f3f4f6' : '#1f2937',
                      fontSize: windowWidth < 576 ? '14px' : '15px',
                      lineHeight: '1.5',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                    }}
                    placeholder="Your thoughts here..."
                    defaultValue={response?.textResponse || ''}
                    onChange={(e) => handleTextChange(question.id, e.target.value, e)}
                    onFocus={(e) => handleTextareaFocus(question.id, e)}
                    onBlur={(e) => handleTextareaBlur(question.id, e)}
                    disabled={isSkipped}
                  />
                </div>
              )}
              
              {/* Skip button */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                marginTop: windowWidth < 576 ? '8px' : '10px' 
              }}>
                <button
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: windowWidth < 576 ? '5px 8px' : '6px 10px',
                    background: isSkipped ? 'rgba(150, 150, 150, 0.1)' : 'transparent',
                    border: `1px solid ${isSkipped ? 'rgba(150, 150, 150, 0.2)' : theme.colors.border}`,
                    borderRadius: theme.borderRadius.small,
                    color: isSkipped ? 'rgba(150, 150, 150, 0.5)' : theme.colors.textSecondary,
                    fontSize: windowWidth < 576 ? '11px' : '12px',
                    fontWeight: '500',
                    cursor: isSkipped ? 'not-allowed' : 'pointer',
                    transition: theme.transitions.medium,
                  }}
                  onClick={() => handleSkipQuestion(question.id)}
                  disabled={isSkipped}
                  title={isSkipped ? "Already skipped" : "Skip this question"}
                >
                  <SkipForward size={14} />
                  Skip
                </button>
              </div>
            </div>
          );
        })}
      </div>
      
      <button
        style={{
          background: 'linear-gradient(to right, #FF3B30, #FF2D55)',
          color: '#fff',
          border: 'none',
          borderRadius: '12px',
          padding: '16px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: submitting || alreadySubmitted ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          marginTop: '10px',
          opacity: submitting || alreadySubmitted ? 0.7 : 1,
          boxShadow: theme.shadows.button,
        }}
        onClick={submitFeedback}
        disabled={submitting || alreadySubmitted}
        onMouseEnter={(e) => {
          if (!submitting && !alreadySubmitted) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = theme.shadows.buttonHover;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = theme.shadows.button;
        }}
      >
        {submitting ? (
          <>
            <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
            Submitting...
          </>
        ) : alreadySubmitted ? (
          <>
            Already Submitted
            <CheckCircle size={18} />
          </>
        ) : (
          <>
            Submit Feedback
            <Send size={18} />
          </>
        )}
      </button>
    </div>
  );
  
  // Main feedback tab content
  const FeedbackTab = () => {
    // Show loading state
    if (loading) {
      return (
        <div style={{
          minHeight: '400px',
          display: 'flex',

          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
          }}>
            <Loader size={50} style={{ 
              animation: 'spin 1s linear infinite',
              color: theme.colors.primary,
            }} />
          </div>
          <p style={{
            color: theme.colors.textSecondary,
            fontSize: '16px',
            textAlign: 'center',
          }}>
            Loading feedback form...
          </p>
        </div>
      );
    }
    
    // Check if already submitted and show appropriate message
    if (alreadySubmitted && !success) {
      return <AlreadySubmittedMessage />;
    }
    
    // Show empty state if no questions
    if (questions.length === 0) {
      return (
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '20px',
        }}>
          <div style={{
            background: theme.colors.card,
            borderRadius: theme.borderRadius.large,
            boxShadow: theme.shadows.medium,
            border: `1px solid ${theme.colors.border}`,
            padding: '40px 20px',
            textAlign: 'center',
          }}>
            <Info size={60} style={{ color: theme.colors.textSecondary, opacity: 0.5, marginBottom: '20px' }} />
            <h3 style={{
              fontSize: '22px',
              fontWeight: '600',
              color: theme.colors.text,
              marginBottom: '15px',
            }}>No Active Questions</h3>
            <p style={{
              color: theme.colors.textSecondary,
              maxWidth: '400px',
              margin: '0 auto',
              fontSize: '16px',
              lineHeight: 1.6,
            }}>
              There are currently no active questions in this feedback form.
              Please check back later or contact the administrator.
            </p>
          </div>
        </div>
      );
    }
    
    // Check if mockup is visible (desktop with wide enough screen)
    const isMockupVisible = isDesktop && windowWidth > 1440 && (!showSidebar || windowWidth > 1600);
    
    // Show feedback form based on view mode
    return (
      <div style={{
        maxWidth: isMockupVisible ? '500px' : '900px',
        margin: '0 auto',
        padding: '20px',
        transition: 'max-width 0.3s ease',
      }}>
        {/* Decorative separator line when mockup is visible */}
        {isMockupVisible && (
          <div style={{
            width: '100%',
            height: '1px',
            background: `linear-gradient(to right, transparent, ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}, transparent)`,
            marginBottom: '30px',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: theme.colors.background,
              padding: '0 12px',
              fontSize: '12px',
              color: theme.colors.textTertiary,
              fontWeight: '500',
              letterSpacing: '0.5px',
            }}>
              YOUR FEEDBACK
            </div>
          </div>
        )}
        
        {/* Intro card - only shown on standard and compact views */}
        {viewMode !== 'step' && (
          <div style={{
            padding: '25px',
            background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.1), rgba(255, 45, 85, 0.05))',
            borderRadius: theme.borderRadius.large,
            marginBottom: '30px',
            border: `1px solid ${darkMode ? 'rgba(255, 59, 48, 0.2)' : 'rgba(255, 59, 48, 0.1)'}`,
          }}>
            <h2 style={{
              fontSize: '22px',
              fontWeight: '700',
              marginBottom: '15px',
              color: theme.colors.text,
            }}>We Value Your Feedback</h2>
            <p style={{
              fontSize: '16px',
              color: theme.colors.textSecondary,
              lineHeight: 1.6,
              margin: 0,
            }}>
              Your responses help us improve our services. All submissions are completely anonymous.
              If you prefer not to answer certain questions, you can skip them.
            </p>
          </div>
        )}
        
        {/* Privacy notice banner - only shown on step view when mockup is NOT visible */}
        {viewMode === 'step' && !isMockupVisible && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(52, 199, 89, 0.08)',
            borderRadius: '10px',
            marginBottom: '20px',
            border: '1px solid rgba(52, 199, 89, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              flexShrink: 0,
              borderRadius: '50%',
              background: 'rgba(52, 199, 89, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Lock size={16} color="#34C759" />
            </div>
            <div>
              <p style={{
                fontSize: '13px',
                fontWeight: '600',
                color: darkMode ? '#E8F5EE' : '#1E5A32',
                margin: '0 0 3px',
              }}>
                Anonymous Feedback
              </p>
              <p style={{
                fontSize: '12px',
                color: darkMode ? 'rgba(200, 230, 215, 0.7)' : 'rgba(46, 123, 75, 0.7)',
                margin: 0,
              }}>
                Your responses are completely private.{' '}
                <button 
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#34C759',
                    textDecoration: 'underline',
                    padding: 0,
                    cursor: 'pointer',
                    fontSize: 'inherit',
                    fontWeight: '500',
                  }}
                  onClick={() => setPrivacyOpen(true)}
                >
                  Learn more
                </button>
              </p>
            </div>
          </div>
        )}
        
        {/* Render the appropriate form view based on viewMode */}
        {viewMode === 'standard' && <StandardFeedbackForm />}
        {viewMode === 'step' && <StepFeedbackForm />}
        {viewMode === 'compact' && <CompactFeedbackForm />}
      </div>
    );
  };
  
  // Landing Page Component
  const LandingPage = () => {
    const [showText, setShowText] = useState(true); // Direkt auf true setzen
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [swipeProgress, setSwipeProgress] = useState(0);
    const [isExiting, setIsExiting] = useState(false);
    
    // Handle exit with animation
    const handleExit = () => {
      setIsExiting(true);
      setTimeout(() => {
        setShowLandingPage(false);
      }, 600); // Match animation duration
    };
    
    // Swipe detection for mobile
    const minSwipeDistance = 50;
    
    const onTouchStart = (e) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientY);
    };
    
    const onTouchMove = (e) => {
      setTouchEnd(e.targetTouches[0].clientY);
      if (touchStart) {
        const distance = touchStart - e.targetTouches[0].clientY;
        if (distance > 0) {
          setSwipeProgress(Math.min(distance / 300, 1));
        }
      }
    };
    
    const onTouchEnd = () => {
      if (!touchStart || !touchEnd) return;
      const distance = touchStart - touchEnd;
      const isUpSwipe = distance > minSwipeDistance;
      
      if (isUpSwipe) {
        handleExit();
      }
      setSwipeProgress(0);
    };
    
    // Scroll detection for desktop
    const onWheel = (e) => {
      if (e.deltaY > 50) {
        // Scrolling down
        handleExit();
      }
    };
    
    // Choose image based on screen size
    const backgroundImage = windowWidth < 768 ? landingImageMobile : landingImageDesktop;
    
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          backgroundColor: darkMode ? '#000' : '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transform: isExiting ? 'translateY(-100%)' : `translateY(-${swipeProgress * 100}%)`,
          opacity: isExiting ? 0 : 1,
          transition: isExiting 
            ? 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s ease' 
            : (swipeProgress === 0 ? 'transform 0.3s ease' : 'none'),
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onWheel={onWheel}
      >
        {/* Background Image */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: showText ? 'brightness(0.4)' : 'brightness(0.7)',
          transition: 'filter 1s ease',
        }}></div>
        
        {/* Overlay Content */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '40px',
          padding: '20px',
          maxWidth: '600px',
          textAlign: 'center',
        }}>
          {/* Text */}
          <div>
            <h1 style={{
              fontSize: windowWidth < 576 ? '32px' : '48px',
              fontWeight: 800,
              color: '#fff',
              marginBottom: '20px',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
              lineHeight: 1.2,
            }}>
              Dein Feedback ist wichtig
            </h1>
            <p style={{
              fontSize: windowWidth < 576 ? '16px' : '20px',
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: 1.6,
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
            }}>
              Hilf uns, das Camp-Erlebnis zu verbessern. Deine Meinung zählt!
            </p>
          </div>
          
          {/* Start Button */}
          <button
            onClick={handleExit}
            style={{
              padding: '18px 48px',
              fontSize: '18px',
              fontWeight: 600,
              color: '#fff',
              background: 'linear-gradient(135deg, #FF3B30, #FF2D55)',
              border: 'none',
              borderRadius: '50px',
              cursor: 'pointer',
              boxShadow: '0 8px 30px rgba(255, 59, 48, 0.4)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(255, 59, 48, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(255, 59, 48, 0.4)';
            }}
          >
            <MessageCircle size={24} />
            Feedback starten
          </button>
          
          {/* Skip Link (optional) */}
          <button
            onClick={handleExit}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'underline',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
            }}
          >
            Überspringen
          </button>
        </div>
        
        {/* Swipe Indicator - bleibt immer sichtbar */}
        <div style={{
          position: 'absolute',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px',
          opacity: 0.6,
        }}>
          <div style={{
            fontSize: '14px',
            color: '#fff',
            textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
          }}>
            Wische nach oben
          </div>
          <div style={{
            // animation: 'bounce 22s infinite',
          }}>
            <ArrowRight size={24} color="#fff" style={{ transform: 'rotate(90deg)' }} />
          </div>
        </div>
      </div>
    );
  };
  
  // Main render function
  return (
    <>
      {/* Show Landing Page first */}
      {showLandingPage && <LandingPage />}
      
      {/* Main Feedback Form */}
      <div style={{
        backgroundColor: theme.colors.background,
        color: theme.colors.text,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        minHeight: '100vh',
zIndex:99,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Background pattern */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: darkMode 
            ? 'radial-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px)'
            : 'radial-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          zIndex: 0,
          pointerEvents: 'none',
        }}></div>
        
        {/* Background gradient */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: darkMode
            ? 'radial-gradient(circle at 100% 0%, rgba(255, 59, 48, 0.08) 0%, transparent 50%), radial-gradient(circle at 0% 100%, rgba(255, 45, 85, 0.08) 0%, transparent 50%)'
            : 'radial-gradient(circle at 100% 0%, rgba(255, 59, 48, 0.04) 0%, transparent 50%), radial-gradient(circle at 0% 100%, rgba(255, 45, 85, 0.04) 0%, transparent 50%)',
          zIndex: 0,
          pointerEvents: 'none',
        }}></div>
        
        {/* Header */}
        <Header />
        
        {/* Sidebar - Desktop und Mobile */}
        {showSidebar && <DesktopSidebar />}
        
        {/* Overlay für Mobile Sidebar */}
        {!isDesktop && showSidebar && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
              animation: 'fadeIn 0.3s ease',
            }}
            onClick={() => setShowSidebar(false)}
          />
        )}
        
        {/* Main content container - volle Breite wie Header */}
        <div 
          style={{
            marginLeft: isDesktop && showSidebar ? '320px' : 0,
            marginTop: '80px',
            paddingBottom: '40px',
            transition: 'margin-left 0.3s ease',
            position: 'relative',
            zIndex: 10,
            minHeight: 'calc(100vh - 120px)',
          }}
        >
          {/* Feedback content - zentriert, volle Breite */}
          <div 
            ref={mainContentRef}
            className="mobile-content custom-scrollbar"
            style={{
              width: '100%',
              maxWidth: isDesktop && windowWidth > 1440 ? '900px' : '900px',
              margin: '0 auto',
              padding: '0 20px',
            }}
          >
            {success ? (
              <SuccessView />
            ) : (
              <>
                {activeTab === 'feedback' && <FeedbackTab />}
                {activeTab === 'info' && <AboutFeedbackTab />}
                {activeTab === 'help' && <HelpSupportTab />}
              </>
            )}
            
            {/* Mockup Display im Mobile - unter dem Submit Button */}
            {!isDesktop && (
              <div style={{
                marginTop: '40px',
                display: 'flex',
                justifyContent: 'center',
              }}>
                <SimplifiedMockupDisplay />
              </div>
            )}
          </div>
          
          {/* Mockup Display im Desktop - Fixed rechts, zentriert in rechter Bildschirmhälfte */}
          {isDesktop && windowWidth > 1440 && (
            <div style={{
              position: 'fixed',
              right: `${(windowWidth - (showSidebar ? 320 : 0)) * 0.25}px`,
              top: '50%',
              transform: 'translate(50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              zIndex: 5,
            }}>
              <SimplifiedMockupDisplay />
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div style={{
          textAlign: 'center',
          padding: '20px 0',
          color: theme.colors.textSecondary,
          fontSize: '13px',
          borderTop: `1px solid ${theme.colors.border}`,
          position: 'relative',
          zIndex: 1,
          background: darkMode ? 'rgba(20, 20, 20, 0.7)' : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
        }}>
          <p style={{ margin: '0 0 10px' }}>
            © 2025 Sunrise LLC. All rights reserved.
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '15px',
          }}>
            <button
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.colors.textSecondary,
                cursor: 'pointer',
                padding: 0,
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
              onClick={() => setPrivacyOpen(true)}
            >
              <Shield size={14} />
              Privacy Policy
            </button>
            <a
              href="https://www.sunrise.ch"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.colors.textSecondary,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <ExternalLink size={14} />
              Sunrise
            </a>
          </div>
        </div>
        
        {/* Privacy Policy Modal */}
        <PrivacyPolicyModal />
        
        {/* Error notification */}
        <ErrorNotification message={error} />
      </div>
    </>
  );
};

export default FeedbackFormWithMockups;